/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {debounce} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import MuiTooltip, {TooltipProps} from '@material-ui/core/Tooltip';
import isNil from 'lodash.isnil';
import {useEventListener} from '@ion/core/src/hooks/useEventListener';
import {GenericObject} from '@ion/core/src/types/entities';
import {useIsPhone} from '@ion/dls/src/hooks/useMedia';
import {DLSShadowStyle} from '@ion/dls/src/types/types';
import {toggleThemeStyle} from '../../../src/theme/helpers';
import {
  dlsAlt,
  dlsColor,
  dlsShadow,
  dlsTypography
} from '../../../src/theme/theme';
import useThemeContext from '../../../src/theme/useThemeContext';

interface StyleProps {
  top?: string;
  width?: string;
  left?: string;
};

export type TooltipVariant = 'default' | 'floating';

export const useNavigationStyle = makeStyles((theme) => ({
  tooltip : {
    ...dlsTypography('caption'),
    backgroundColor : dlsAlt('colors', 'tooltipBackground'),
    color           : dlsColor('white'),
    letterSpacing   : '1px',
    fontWeight      : 500,
    padding         : '8px',
    marginLeft      : '10px',
    fontSize        : '10px !important',
    lineHeight      : '20px !important',
    minWidth        : 120,
    textAlign       : 'left',
    fontFamily      : 'Neo Sans Std',
    textTransform   : 'uppercase',
    boxShadow       : '0px 3px 5px 0px #00000033, 0px 1px 18px 0px #0000001F, 0px 6px 10px 0px #00000024'
  },
  arrow : {
    color : dlsAlt('colors', 'tooltipBackground')
  }
}));

const useStyles = makeStyles((theme) => ({
  tooltip : toggleThemeStyle({
    ...dlsTypography('caption'),
    backgroundColor : dlsAlt('colors', 'tooltipBackground'),
    color           : dlsColor('white'),
    letterSpacing   : '0.4px',
    fontWeight      : 400,
    padding         : '4px 8px',
    fontSize        : '12px !important',
    lineHeight      : '16px !important',
    minWidth        : 100,
    textAlign       : 'center',
    top             : ({top}: StyleProps) => top || 'initial',
    left            : ({left}: StyleProps) => left || 'initial',
    width           : ({width}: StyleProps) => width || 'auto'
  }, {
    top             : ({top}: StyleProps) => top || 'initial',
    left            : ({left}: StyleProps) => left || 'initial',
    color           : dlsColor('white'),
    width           : ({width}: StyleProps) => width || 'auto',
    filter          : dlsShadow(1, DLSShadowStyle.DropShadow),
    padding         : '4px 8px',
    fontSize        : '12px !important',
    minWidth        : 0,
    boxShadow       : dlsShadow(1),
    textAlign       : 'center',
    minHeight       : '24px',
    // was relying purely on minHeight/padding/line-height adding up to
    // exactly the same value to *look* vertically centered - any content
    // that doesn't fit that exact math (wraps, or a font-metric rounding
    // difference) left the text sitting at the top of the box instead.
    // flex + center makes this an actual centering rule, not a numeric
    // coincidence, for every current-theme tooltip (Zoom In/Out and any
    // other widget sharing this component) ...
    display         : 'flex',
    alignItems      : 'center',
    justifyContent  : 'center',
    // long text wraps inside the box - also within a word with no break in
    // it, which in this flex box otherwise ran out past the tooltip's edge ...
    overflowWrap    : 'anywhere',
    fontFamily      : 'Inter',
    fontWeight      : 500,
    lineHeight      : '16px !important',
    // MUI's own `MuiTooltip-tooltip` rule is injected after this sheet and
    // sets a 4px radius, so the DLS radius only holds as `!important` -
    // the same reason `lineHeight` and `fontSize` above carry it ...
    borderRadius    : '8px !important',
    letterSpacing   : 0,
    backgroundColor : dlsColor('grey1')
  }),
  arrow : toggleThemeStyle({
    color : dlsAlt('colors', 'tooltipBackground')
  }, {
    color : dlsColor('grey1')
  })
}));

const useFloatingStyles = makeStyles((theme) => ({
  tooltip : {
    backgroundColor : dlsColor('white'),
    border          : `1px solid ${dlsColor('grey5')}`,
    // outranks MUI's 4px radius, as in the default variant above ...
    borderRadius    : '8px !important',
    boxShadow       : dlsShadow(1.4),
    color           : dlsColor('grey1'),
    filter          : dlsShadow(1.4, DLSShadowStyle.DropShadow),
    fontFamily      : 'Inter',
    fontSize        : '12px !important',
    fontWeight      : 500,
    letterSpacing   : 0,
    lineHeight      : '16px !important',
    minHeight       : '24px',
    minWidth        : 0,
    opacity         : 0.8,
    padding         : '4px 8px',
    textAlign       : 'center',
    // display:flex + center, not padding/line-height math, is what
    // actually centers the label vertically - see the default variant's
    // tooltip rule above for the same fix ...
    display         : 'flex',
    alignItems      : 'center',
    justifyContent  : 'center',
    top             : ({top}: StyleProps) => top || 'initial',
    left            : ({left}: StyleProps) => left || 'initial',
    width           : ({width}: StyleProps) => width || 'auto'
  },
  arrow : {
    color : dlsColor('white')
  }
}));

export interface TooltipTypes extends TooltipProps, StyleProps {
  renderOnlyChildren?: boolean;
  isOpen?: boolean;
  externalClasses?: GenericObject;
  // merged into MUI's own popper class (keeping its arrow and pointer
  // rules), e.g. to lift the popper above a drawer ...
  popperClassName?: string;
  variant?: TooltipVariant;
};

/**
 * @Tooltip
 *
 * Defines the Tooltip component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 *
 * import React from 'react';
 * import {Tooltip} from '@ion/dls/src/tooltip';
 * import DeleteIcon from '@material-ui/icons/Delete';
 * import IconButton from '@material-ui/core/IconButton';
 *
 * function MyComponent {
 *   return (
 *     <React.Fragment>
 *      <Tooltip title="Delete">
 *        <IconButton>
 *          <DeleteIcon />
 *        </IconButton>
 *      </Tooltip>
 *     </React.Fragment>
 *   );
 * }
 *
 * ```
 *
 */
const Tooltip = ({
  children,
  top,
  left,
  width,
  variant = 'default',
  externalClasses,
  popperClassName,
  renderOnlyChildren = false,
  isOpen = false,
  ...props
}: TooltipTypes) => {
  const defaultClasses  = useStyles({top, left, width});
  const floatingClasses = useFloatingStyles({top, left, width});
  const {isCurrent}     = useThemeContext();
  // the floating help text is the current theme's; legacy keeps its own ...
  const variantClasses  = variant === 'floating' && isCurrent ? floatingClasses : defaultClasses;
  const baseClasses     = externalClasses ?? variantClasses;
  const classes         = useMemo(() => (
    popperClassName ? {...baseClasses, popper: popperClassName} : baseClasses
  ), [baseClasses, popperClassName]);
  const [open, setOpen] = useState(isOpen);
  const isPhone         = useIsPhone();
  const element         = useMemo(
    () => document.getElementById('base_layout_content'),
    [isPhone]
  );

  // Allows for external setting of open tooltip state
  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  const onHandleScroll = useCallback(debounce((e) => {
    if (e && open) {
      setOpen(false);
    };
  }, 200), [open]);

  useEventListener('scroll', onHandleScroll, element);

  // invoke the method when the user mouse over or click the trigger point...
  const onOpen = function() {
    setOpen(!props.disableHoverListener);
  };

  // invoke the method when the user go away or click outside of trigger point...
  const onClose = useCallback(() => {
    setOpen(false);
  }, []);

  // a click that changes the trigger's own state/DOM out from under the
  // pointer (e.g. a widget button that becomes disabled or re-renders at
  // a zoom limit) can leave the tooltip stuck open, because the browser
  // does not reliably fire mouseout/mouseleave for that case - closing
  // explicitly on click is a safety net independent of that event.
  // Composed onto the child (rather than passed as a prop to MuiTooltip
  // directly, as onMouseOut/onMouseMove are above) because MUI's own
  // internal hover handling overwrites an onMouseLeave passed that way
  // with its own handler, dropping ours ...
  const onChildClick = useCallback((event: React.MouseEvent) => {
    onClose();
    if (React.isValidElement(children)) {
      children.props?.onClick?.(event);
    }
  }, [children, onClose]);

  const onChildMouseLeave = useCallback((event: React.MouseEvent) => {
    onClose();
    if (React.isValidElement(children)) {
      children.props?.onMouseLeave?.(event);
    }
  }, [children, onClose]);

  const childWithCloseHandlers = useMemo(() => (
    React.isValidElement(children)
      ? React.cloneElement(children, {
        onClick      : onChildClick,
        onMouseLeave : onChildMouseLeave
      })
      : children
  ), [children, onChildClick, onChildMouseLeave]);

  if (renderOnlyChildren || isNil(props?.title)) {
    return children;
  }

  return (
    <MuiTooltip open={open}
      classes={classes}
      onMouseMove={onOpen}
      onMouseOut={onClose}
      {...props}>
      {childWithCloseHandlers}
    </MuiTooltip>
  );
};

export default Tooltip;
